import React from 'react';

export type AuthType = 'none' | 'bearer' | 'headers' | 'oauth_dynamic' | 'oauth_pre_registered';

export type TransportType = 'sse' | 'http';

export interface McpReAuthContext {
  url: string;
  mcpName: string;
  authType?: AuthType;
  transportType?: TransportType;
  /** When reconnecting after failure, replace this MCP row on connect. */
  mcpId?: number;
}

export interface tuneModalProps {
  onClose: () => void;
  webSearchEnabled: boolean;
  onWebSearchToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  open: boolean;
  onSave?: () => void;
  reAuth?: McpReAuthContext;
  /** Prefill connect form (Connect to MCP Server) without switching to reconnect mode. */
  prefill?: McpReAuthContext;
}

export interface MCPServerConfig {
  authType: AuthType;
  url: string;
  name: string;
  token: string;
  clientId: string;
  clientSecret: string;
  transportType: TransportType;
}

export interface ValidationErrors {
  headers?: string;
  name?: string;
  url?: string;
  token?: string;
  clientId?: string;
  key?: string;
  value?: string;
  [key: string]: string | undefined;
}

export interface MCPServerPayload {
  name: string;
  auth_type: AuthType;
  url: string;
  transportType?: string;
  token?: string;
  headers?: Record<string, string>;
  client_id?: string;
  client_secret?: string;
  reconnect_mcp_id?: number;
}

export const MCP_AUTH_TYPE_OPTIONS: { value: AuthType; label: string }[] = [
  { value: 'none', label: 'No Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'headers', label: 'Custom Headers' },
  { value: 'oauth_dynamic', label: 'OAuth (Dynamic Registration)' },
  { value: 'oauth_pre_registered', label: 'OAuth (Pre-registered Client)' },
];

export const isOAuthAuthType = (authType: AuthType): boolean =>
  authType === 'oauth_dynamic' || authType === 'oauth_pre_registered';

const VALID_AUTH_TYPES = new Set<AuthType>(MCP_AUTH_TYPE_OPTIONS.map((option) => option.value));

/** Map API/legacy auth_type values to the current AuthType union. */
export const normalizeAuthType = (authType?: string): AuthType => {
  if (authType === 'oauth') {
    return 'oauth_dynamic';
  }
  if (authType && VALID_AUTH_TYPES.has(authType as AuthType)) {
    return authType as AuthType;
  }
  return 'none';
};

/** Compact label for MCP list badges (OAuth variants → "OAuth", none → "No Auth"). */
export const getMcpAuthTypeLabel = (authType?: string): string => {
  const normalized = normalizeAuthType(authType);
  switch (normalized) {
    case 'oauth_dynamic':
    case 'oauth_pre_registered':
      return 'OAuth';
    case 'none':
      return 'No Auth';
    case 'bearer':
      return 'Bearer';
    case 'headers':
      return 'Headers';
    default:
      return 'Unknown';
  }
};

export const getMcpStatusBadge = (status?: string) => {
  if (status === 'active') {
    return { className: 'bg-green-100 text-green-700', label: 'active' };
  }
  if (status === 'unauthorize') {
    return { className: 'bg-amber-100 text-amber-700', label: 'Unauthorized' };
  }
  if (status === 'offline') {
    return { className: 'bg-red-100 text-red-700', label: 'Offline' };
  }
  if (status === 'inactive') {
    return { className: 'bg-gray-100 text-gray-600', label: 'Off' };
  }
  return { className: 'bg-gray-100 text-gray-600', label: status || 'inactive' };
};

export const EMPTY_MCP_RE_AUTH: McpReAuthContext = { url: '', mcpName: '' };
